import { Scenario, Side, OpponentPosition, Zone, BallDepth } from './types';

/**
 * Directional tennis tactics:
 *
 * 1. DEEP BALL, NEUTRAL RALLY → cross-court deep (high percentage)
 * 2. OPPONENT PULLED WIDE → go behind them or to open court, deep
 * 3. SHORT BALL → attack! approach to open court, angle, or drop shot
 * 4. OPPONENT AT NET → pass down the line or cross, always deep
 * 5. NEVER go for winners from a neutral deep ball position
 */

interface ScenarioTemplate {
  opponentPos: OpponentPosition;
  ballDepth: BallDepth;
  ballDirection: Zone;
  correctReturn: Zone;
  tactic: string;
}

const TEMPLATES: ScenarioTemplate[] = [
  // ===== DEEP BALL RALLIES (high percentage = cross-court) =====

  // Neutral rally — cross-court deep is always the right play
  {
    opponentPos: 'baseline-center',
    ballDepth: 'deep',
    ballDirection: 'cross-deep',
    correctReturn: 'cross-deep',
    tactic: 'Deep ball, neutral rally → cross-court deep',
  },
  {
    opponentPos: 'baseline-center',
    ballDepth: 'deep',
    ballDirection: 'line-deep',
    correctReturn: 'cross-deep',
    tactic: 'Deep ball → rally cross-court, don\'t go for a winner',
  },
  {
    opponentPos: 'baseline-center',
    ballDepth: 'deep',
    ballDirection: 'middle-deep',
    correctReturn: 'cross-deep',
    tactic: 'Deep ball to middle → cross-court deep is safest',
  },

  // Opponent pulled to deuce side — open court is ad, but go behind them if deep
  {
    opponentPos: 'baseline-deuce',
    ballDepth: 'deep',
    ballDirection: 'cross-deep',
    correctReturn: 'cross-deep',
    tactic: 'Opponent on deuce side, deep ball → keep it cross-court behind them',
  },
  {
    opponentPos: 'baseline-wide-deuce',
    ballDepth: 'deep',
    ballDirection: 'cross-deep',
    correctReturn: 'line-deep',
    tactic: 'Opponent pulled wide → now you can change direction down the line',
  },

  // Opponent pulled to ad side
  {
    opponentPos: 'baseline-ad',
    ballDepth: 'deep',
    ballDirection: 'line-deep',
    correctReturn: 'cross-deep',
    tactic: 'Opponent on ad side, deep ball → cross-court to open space',
  },
  {
    opponentPos: 'baseline-wide-ad',
    ballDepth: 'deep',
    ballDirection: 'line-deep',
    correctReturn: 'line-deep',
    tactic: 'Opponent pulled wide ad → change direction down the line',
  },

  // Opponent slightly off-center — still rally, don't go for hero shot
  {
    opponentPos: 'baseline-deuce',
    ballDepth: 'deep',
    ballDirection: 'middle-deep',
    correctReturn: 'cross-deep',
    tactic: 'Deep ball, opponent slightly off-center → still cross-court deep',
  },
  {
    opponentPos: 'baseline-ad',
    ballDepth: 'deep',
    ballDirection: 'middle-deep',
    correctReturn: 'cross-deep',
    tactic: 'Deep ball → don\'t try to hit a winner, rally cross-court',
  },

  // ===== SHORT BALL = ATTACK =====

  // Short ball, opponent at baseline center — approach to open court
  {
    opponentPos: 'baseline-center',
    ballDepth: 'short',
    ballDirection: 'middle-short',
    correctReturn: 'line-deep',
    tactic: 'Short ball! → approach deep to their weaker side',
  },
  {
    opponentPos: 'baseline-center',
    ballDepth: 'short',
    ballDirection: 'cross-short',
    correctReturn: 'cross-deep',
    tactic: 'Short ball! → attack deep cross-court, take the net',
  },

  // Short ball, opponent deep — drop shot or angle
  {
    opponentPos: 'baseline-center',
    ballDepth: 'short',
    ballDirection: 'middle-short',
    correctReturn: 'cross-short',
    tactic: 'Short ball, opponent deep → angle it short to pull them forward',
  },

  // Short ball, opponent pulled wide — go to open court
  {
    opponentPos: 'baseline-wide-deuce',
    ballDepth: 'short',
    ballDirection: 'middle-short',
    correctReturn: 'line-deep',
    tactic: 'Short ball, opponent wide → attack the open court deep',
  },
  {
    opponentPos: 'baseline-wide-ad',
    ballDepth: 'short',
    ballDirection: 'middle-short',
    correctReturn: 'cross-deep',
    tactic: 'Short ball, opponent wide → attack the open court deep',
  },
  {
    opponentPos: 'baseline-deuce',
    ballDepth: 'short',
    ballDirection: 'cross-short',
    correctReturn: 'line-deep',
    tactic: 'Short ball! → approach deep to the open side',
  },
  {
    opponentPos: 'baseline-ad',
    ballDepth: 'short',
    ballDirection: 'line-short',
    correctReturn: 'cross-deep',
    tactic: 'Short ball! → approach deep to the open side',
  },

  // ===== OPPONENT AT NET = PASS =====

  // Opponent at net deuce — pass down the line
  {
    opponentPos: 'net-deuce',
    ballDepth: 'short',
    ballDirection: 'cross-short',
    correctReturn: 'line-deep',
    tactic: 'Opponent at net on deuce → pass down the line deep',
  },
  {
    opponentPos: 'net-deuce',
    ballDepth: 'deep',
    ballDirection: 'cross-deep',
    correctReturn: 'line-deep',
    tactic: 'Opponent at net → pass down the line, keep it deep',
  },

  // Opponent at net ad — pass cross-court
  {
    opponentPos: 'net-ad',
    ballDepth: 'short',
    ballDirection: 'line-short',
    correctReturn: 'cross-deep',
    tactic: 'Opponent at net on ad → pass cross-court deep',
  },
  {
    opponentPos: 'net-ad',
    ballDepth: 'deep',
    ballDirection: 'line-deep',
    correctReturn: 'cross-deep',
    tactic: 'Opponent at net → pass cross-court deep',
  },

  // Opponent at net center — go to the bigger gap
  {
    opponentPos: 'net-center',
    ballDepth: 'short',
    ballDirection: 'middle-short',
    correctReturn: 'line-deep',
    tactic: 'Opponent at net center → pass down the line deep',
  },
  {
    opponentPos: 'net-center',
    ballDepth: 'deep',
    ballDirection: 'middle-deep',
    correctReturn: 'cross-deep',
    tactic: 'Opponent at net → low cross-court pass',
  },
];

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

export function generateScenario(streak: number): Scenario {
  const template = pick(TEMPLATES);
  const side = pick<Side>(['deuce', 'ad']);

  // Time limit: starts generous, tightens with streak, floor at 1.2s
  const timeLimit = Math.max(1200, 3000 - streak * 100);

  return {
    side,
    ...template,
    timeLimit,
  };
}
